import { createReducer, on } from "@ngrx/store";
import { onLoadableLoad, onLoadableSuccess, onLoadableError } from '../../models/index';
import { IAssetTag } from '../../models/tag.interface';
import { initialTagState, TagState } from "./tag.state";
import { TagActions } from "./tag.actions";
import { UserActions } from "../user/user.actions";

type AssetTagMap = TagState['assetTagMap'];

function mapAssetTags(assetTagMap: AssetTagMap, transform: (assetTags: IAssetTag[]) => IAssetTag[]): AssetTagMap {
    const result: AssetTagMap = {};
    for (const key in assetTagMap) {
        result[key] = { ...assetTagMap[key], assetTags: transform(assetTagMap[key].assetTags) };
    }
    return result;
}

export const tagReducer = createReducer(
    initialTagState,

    on(TagActions.loadTags, state => ({ ...onLoadableLoad(state) })),
    on(TagActions.loadTagsSuccess, (state, { tags }) => ({ ...onLoadableSuccess(state), tags })),
    on(TagActions.loadTagsFailure, (state, { error }) => ({ ...onLoadableError(state, error) })),

    on(TagActions.createTag, state => ({ ...state, saving: onLoadableLoad(state.saving) })),
    on(TagActions.createTagSuccess, (state, { tag }) => ({
        ...state,
        tags: [...state.tags, tag],
        saving: onLoadableSuccess(state.saving),
    })),
    on(TagActions.createTagFailure, (state, { error }) => ({
        ...state,
        saving: onLoadableError(state.saving, error),
    })),

    on(TagActions.updateTags, state => ({ ...state, saving: onLoadableLoad(state.saving) })),
    on(TagActions.updateTagsSuccess, (state, { tags: updatedTags }) => ({
        ...state,
        tags: state.tags.map(existing => {
            const updated = updatedTags.find(u => u.id === existing.id);
            return updated ?? existing;
        }),
        assetTagMap: mapAssetTags(state.assetTagMap, assetTags => assetTags.map(assetTag => {
            const updated = updatedTags.find(u => u.id === assetTag.tag_id);
            return updated ? { ...assetTag, tag: updated } : assetTag;
        })),
        saving: onLoadableSuccess(state.saving),
    })),
    on(TagActions.updateTagsFailure, (state, { error }) => ({
        ...state,
        saving: onLoadableError(state.saving, error),
    })),

    on(TagActions.deleteTags, state => ({ ...state, saving: onLoadableLoad(state.saving) })),
    on(TagActions.deleteTagsSuccess, (state, { tagIds }) => ({
        ...state,
        tags: state.tags.filter(t => !tagIds.includes(t.id)),
        assetTagMap: mapAssetTags(state.assetTagMap, assetTags => assetTags.filter(assetTag => !tagIds.includes(assetTag.tag_id))),
        saving: onLoadableSuccess(state.saving),
    })),
    on(TagActions.deleteTagsFailure, (state, { error }) => ({
        ...state,
        saving: onLoadableError(state.saving, error),
    })),

    on(TagActions.loadColoursSuccess, (state, { colours }) => ({ ...state, colours })),

    on(TagActions.loadAssetTags, (state, { assetType, assetId }) => {
        const key = `${assetType}:${assetId}`;
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags: state.assetTagMap[key]?.assetTags ?? [], loading: true, error: null },
            },
        };
    }),
    on(TagActions.loadAssetTagsSuccess, (state, { assetType, assetId, assetTags }) => {
        const key = `${assetType}:${assetId}`;
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags, loading: false, error: null },
            },
        };
    }),
    on(TagActions.loadAssetTagsFailure, (state, { assetType, assetId, error }) => {
        const key = `${assetType}:${assetId}`;
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags: state.assetTagMap[key]?.assetTags ?? [], loading: false, error },
            },
        };
    }),

    on(TagActions.assignAssetTags, (state, { assetType, assetId, tagIds }) => {
        const key = `${assetType}:${assetId}`;
        const existing = state.assetTagMap[key]?.assetTags ?? [];
        const optimistic: IAssetTag[] = tagIds
            .filter(tid => !existing.some(at => at.tag_id === tid))
            .map(tid => {
                const tag = state.tags.find(t => t.id === tid);
                return {
                    id: `optimistic-${tid}`,
                    tag_id: tid,
                    asset_id: assetId,
                    asset_type: assetType,
                    assigned_by: null,
                    creation_timestamp: new Date().toISOString(),
                    tag: tag ?? { id: tid, name: '', colour: '#999999', description: null, is_quick_tag: false, organisation_id: '', created_by: '', updated_by: null, creation_timestamp: '', update_timestamp: null },
                };
            });
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags: [...existing, ...optimistic], loading: false, error: null },
            },
        };
    }),

    on(TagActions.assignAssetTagsSuccess, (state, { assetType, assetId, assetTags }) => {
        const key = `${assetType}:${assetId}`;
        const existing = state.assetTagMap[key]?.assetTags ?? [];
        const withoutOptimistic = existing.filter(at => !at.id.startsWith('optimistic-') || !assetTags.some(real => real.tag_id === at.tag_id));
        const combined = [...withoutOptimistic, ...assetTags];
        const deduped = Array.from(new Map(combined.map(at => [at.tag_id, at])).values());
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags: deduped, loading: false, error: null },
            },
        };
    }),

    on(TagActions.assignAssetTagsFailure, (state, { assetType, assetId, tagIds, error }) => {
        const key = `${assetType}:${assetId}`;
        const existing = state.assetTagMap[key]?.assetTags ?? [];
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags: existing.filter(at => !tagIds.includes(at.tag_id) || !at.id.startsWith('optimistic-')), loading: false, error },
            },
        };
    }),

    on(TagActions.unassignAssetTags, (state, { assetType, assetId, tagIds }) => {
        const key = `${assetType}:${assetId}`;
        const existing = state.assetTagMap[key]?.assetTags ?? [];
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags: existing.filter(at => !tagIds.includes(at.tag_id)), loading: false, error: null },
            },
        };
    }),

    on(TagActions.unassignAssetTagsSuccess, (state, { assetType, assetId }) => {
        const key = `${assetType}:${assetId}`;
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { ...state.assetTagMap[key], error: null },
            },
        };
    }),

    on(TagActions.unassignAssetTagsFailure, (state, { assetType, assetId, error }) => {
        const key = `${assetType}:${assetId}`;
        return {
            ...state,
            assetTagMap: {
                ...state.assetTagMap,
                [key]: { assetTags: state.assetTagMap[key]?.assetTags ?? [], loading: false, error },
            },
        };
    }),

    on(UserActions.logout, () => ({ ...initialTagState })),
);
